"use client";

import React, { useCallback, useMemo } from "react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/shadcn-components/ui/dropdown-menu";
import { Badge } from "@/shadcn-components/ui/badge";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  Filter,
} from "lucide-react";
import {
  formatSignedPnl,
  formatOrderType,
  getTradeClosePrice,
  isMarketTrade,
  isPendingTrade,
  useOpenedTrades,
  useSyncOpenPositionsLiveMetrics,
} from "@/vybe-trader-client/opened-trades";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import { resolveCloseLots } from "@/lib/terminal/engine/shared/orders";
import { useAccountInformationStore } from "@/vybe-trader-client/account-information";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import { useIsInvestorAccount } from "@/vybe-trader-client/account-information/hooks/useAccountInformation";
import { InvestorBadge } from "@/components/common/InvestorBadge";
import type { OpenedTrade } from "@/vybe-trader-client/opened-trades";
import { VybeOrderType } from "@/vybe-trader-client/opened-trades";
import { useOpenPositionsLiveMetricsStore } from "@/vybe-trader-client/opened-trades/stores/useOpenPositionsLiveMetricsStore";
import CloseByPositionDialog from "@/components/trades-view/close-by/CloseByPositionDialog";
import { useDesktopLayoutStore } from "@/lib/desktop-layout/store";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { OpenPositionsTable } from "@/components/open-positions/OpenPositionsTable";
import type { OpenPositionTableRowData } from "@/components/open-positions/types";
import {
  LIVE_TABLE_COLUMNS,
  getRowFilterHaystack,
  getRowSortValue,
} from "@/components/open-positions/openPositionsTableHelpers";
import { OpenPositionsTableHeader } from "@/components/open-positions/OpenPositionsTableHeader";
import { BulkCloseConfirmDialog } from "@/components/open-positions/BulkCloseConfirmDialog";
import {
  selectBulkClose,
  type BulkCloseMode,
} from "@/vybe-trader-client/connection/client/VybeTraderClient";
import { SkeletonRows } from "@/components/common/SkeletonRows";
import { EmptyBlock } from "@/components/common/EmptyBlock";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import TradeOrderScreen from "@/components/trades-view/trade-order/TradeOrderScreen";

const POSITIONS_SKELETON = {
  columnWidths: [62, 46, 46, 46],
  rowCount: 6,
} as const;

export default function TradeDesktopContent() {
  // Get opened trades data
  const { allTrades, loadingStatus } = useOpenedTrades();


  // Get client for closing trades
  const { client } = useVybeTraderClient();
  // Investor (read-only) login: positions stay visible, every close/modify control goes away.
  const isInvestor = useIsInvestorAccount();

  // Sorting and filtering state
  const [sortColumn, setSortColumn] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
    "asc"
  );
  const [filters, setFilters] = React.useState<Record<string, string>>({});
  const [showFilterInputs, setShowFilterInputs] = React.useState(false);
  const [closeByTrade, setCloseByTrade] = React.useState<OpenedTrade | null>(
    null
  );
  // Position whose SL/TP is being edited from the row context menu (SR Global, 2026-09-08: the only
  // way to reach a modify used to be the chart, and nothing could delete a level once set).
  const [modifyTrade, setModifyTrade] = React.useState<OpenedTrade | null>(null);

  const tradesToUse = allTrades;

  const accountCurrency = useAccountInformationStore(
    (state) => state.account?.currency || "USD"
  );

  useSyncOpenPositionsLiveMetrics(tradesToUse, accountCurrency);

  const focusedChartPanelId = useDesktopLayoutStore(
    (s) => s.focusedChartPanelId,
  );
  const focusedTradeTicketFromChart = useChartStore3((state) =>
    state.slots[focusedChartPanelId].focusedTradeTicket,
  );

  const staticRows: OpenPositionTableRowData[] = useMemo(
    () =>
      tradesToUse.map((trade) => ({
        trade,
        tradeType: formatOrderType(trade.orderType),
        isMarket: isMarketTrade(trade),
        isPending: isPendingTrade(trade),
      })),
    [tradesToUse]
  );

  const usesLiveMetricsForSortFilter = useMemo(() => {
    if (sortColumn && LIVE_TABLE_COLUMNS.has(sortColumn)) return true;
    return Object.entries(filters).some(
      ([column, value]) => value && LIVE_TABLE_COLUMNS.has(column)
    );
  }, [sortColumn, filters]);

  const metricsVersion = useOpenPositionsLiveMetricsStore((s) =>
    usesLiveMetricsForSortFilter ? s.version : 0
  );

  const liveTotalProfit = useOpenPositionsLiveMetricsStore((s) => {
    let total = 0;
    for (const m of s.byTicket.values()) total += m.profit;
    return total;
  });
  const profitableTrades = useOpenPositionsLiveMetricsStore((s) => {
    let count = 0;
    for (const m of s.byTicket.values()) if (m.profit > 0) count += 1;
    return count;
  });
  const losingTrades = useOpenPositionsLiveMetricsStore((s) => {
    let count = 0;
    for (const m of s.byTicket.values()) if (m.profit < 0) count += 1;
    return count;
  });

  const liveMetricsByTicket = useOpenPositionsLiveMetricsStore((s) => s.byTicket);

  const [bulkCloseMode, setBulkCloseMode] = React.useState<BulkCloseMode | null>(
    null
  );

  // Sorting function
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const processedRows = useMemo(() => {
    let filtered = [...staticRows];

    Object.entries(filters).forEach(([column, value]) => {
      if (!value) return;
      const q = value.toLowerCase();
      filtered = filtered.filter((row) =>
        getRowFilterHaystack(row, column).includes(q)
      );
    });

    if (sortColumn) {
      filtered.sort((a, b) => {
        const aValue = getRowSortValue(a, sortColumn);
        const bValue = getRowSortValue(b, sortColumn);

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
        }
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        return sortDirection === "asc"
          ? aStr.localeCompare(bStr)
          : bStr.localeCompare(aStr);
      });
    }

    return filtered;
  }, [staticRows, sortColumn, sortDirection, filters, metricsVersion]);

  // Render sort icon
  const renderSortIcon = (column: string) => {
    if (sortColumn !== column)
      return <ArrowUpDown className="ml-1 h-2.5 w-2.5 shrink-0 opacity-60" />;
    return sortDirection === "asc" ? (
      <ArrowUp className="ml-1 h-2.5 w-2.5 shrink-0" />
    ) : (
      <ArrowDown className="ml-1 h-2.5 w-2.5 shrink-0" />
    );
  };

  // Handle filter change
  const handleFilterChange = (column: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({});
  };

  // Toggle filter inputs visibility
  const toggleFilterInputs = () => {
    setShowFilterInputs(!showFilterInputs);
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some((value) => value !== "");

  const hasCloseByCandidate = useCallback(
    (trade: OpenedTrade) => {
      if (!isMarketTrade(trade)) return false;

      const oppositeType =
        trade.orderType === VybeOrderType.Buy
          ? VybeOrderType.Sell
          : VybeOrderType.Buy;

      return staticRows.some(
        (candidate) =>
          candidate.trade.ticket !== trade.ticket &&
          candidate.trade.symbol === trade.symbol &&
          candidate.trade.orderType === oppositeType &&
          candidate.isMarket
      );
    },
    [staticRows]
  );

  // Render filter dropdown for a column
  const renderFilterDropdown = (column: string, placeholder: string) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-3.5 w-3.5 p-0 ml-0.5 ${
            filters[column] ? "text-blue-600" : "text-muted-foreground"
          }`}
        >
          <Filter className="h-2.5 w-2.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-48" align="start">
        <div className="p-1.5">
          <Input
            placeholder={placeholder}
            value={filters[column] || ""}
            onChange={(e) => handleFilterChange(column, e.target.value)}
            className="h-7 text-[11px] px-2"
            autoFocus
          />
          {filters[column] && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-1.5 h-5 w-full text-[11px]"
              onClick={() => handleFilterChange(column, "")}
            >
              Clear filter
            </Button>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const getCurrentPrice = useCallback((trade: OpenedTrade) => {
    if (!isMarketTrade(trade)) {
      return trade.openPrice;
    }
    const subscribedSymbol = useSubscribedSymbolsStore
      .getState()
      .byCurrency.get(trade.symbol);
    return getTradeClosePrice(trade, subscribedSymbol?.quote);
  }, []);

  const handleCloseTrade = useCallback(async (trade: OpenedTrade, lots?: number) => {
    if (!client || !client.isConnected()) {
      console.error("Client not connected");
      return;
    }

    try {
      const volumeStep = useSymbolsStore
        .getState()
        .findSymbolByCurrency(trade.symbol)?.volumeStep;
      const closeData = {
        Ticket: trade.ticket,
        Symbol: trade.symbol,
        Price: getCurrentPrice(trade),
        Lots: resolveCloseLots(trade, lots, volumeStep),
        OrderType: trade.orderType,
        Slippage: 100,
      };

      await client.closeOrder(closeData);
    } catch (error) {
      console.error("Failed to close trade:", error);
    }
  }, [client, getCurrentPrice]);

  const handleCloseBy = useCallback((trade: OpenedTrade) => {
    setCloseByTrade(trade);
  }, []);

  const handleModify = useCallback((trade: OpenedTrade) => {
    setModifyTrade(trade);
  }, []);

  const handleClearLevel = useCallback(
    async (trade: OpenedTrade, level: "SL" | "TP") => {
      if (!client || !client.isConnected()) {
        console.error("Client not connected");
        return;
      }
      try {
        await client.modifyOrder({
          Ticket: trade.ticket,
          Symbol: trade.symbol,
          Lots: trade.lots,
          Price: trade.openPrice,
          OrderType: trade.orderType,
          // The untouched level is re-sent as-is; 0 alone means "not specified" on the wire, so the
          // removal itself travels as an explicit Clear* flag.
          StopLoss: level === "SL" ? 0 : trade.stopLoss || 0,
          TakeProfit: level === "TP" ? 0 : trade.takeProfit || 0,
          ClearStopLoss: level === "SL",
          ClearTakeProfit: level === "TP",
          Comment: `Removed ${level} via MT6 Trading App`,
        });
      } catch (error) {
        console.error(`Failed to remove ${level}:`, error);
      }
    },
    [client]
  );

  // Live open positions annotated with their current profit — the shared input
  // to `selectBulkClose`, which powers both the row menu and the toolbar verbs.
  const bulkClosablePositions = useMemo(
    () =>
      staticRows.map((row) => ({
        trade: row.trade,
        ticket: row.trade.ticket,
        profit: liveMetricsByTicket.get(row.trade.ticket)?.profit ?? 0,
      })),
    [staticRows, liveMetricsByTicket]
  );

  const bulkCounts = useMemo(
    () => ({
      all: selectBulkClose(bulkClosablePositions, "all").tickets.length,
      profitable: selectBulkClose(bulkClosablePositions, "profitable").tickets
        .length,
      losing: selectBulkClose(bulkClosablePositions, "losing").tickets.length,
    }),
    [bulkClosablePositions]
  );

  const bulkSelection = useMemo(
    () =>
      bulkCloseMode
        ? selectBulkClose(bulkClosablePositions, bulkCloseMode)
        : null,
    [bulkCloseMode, bulkClosablePositions]
  );

  const handleBulkClose = useCallback((mode: BulkCloseMode) => {
    setBulkCloseMode(mode);
  }, []);

  const handleConfirmBulkClose = useCallback(async () => {
    const selection = bulkSelection;
    setBulkCloseMode(null);
    if (!selection || selection.positions.length === 0) return;

    if (!client || !client.isConnected()) {
      console.error("[bulk-close] Client not connected");
      return;
    }

    // Fan the chosen tickets through the existing single-close path and collect
    // per-ticket outcomes so a partial failure is surfaced, not swallowed.
    const results = await Promise.allSettled(
      selection.positions.map(({ trade }) =>
        client.closeOrder({
          Ticket: trade.ticket,
          Symbol: trade.symbol,
          Price: getCurrentPrice(trade),
          Lots: trade.lots,
          OrderType: trade.orderType,
          Slippage: 100,
        })
      )
    );

    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      console.error(
        `[bulk-close] ${failed}/${results.length} close request(s) failed`
      );
    }
  }, [bulkSelection, client, getCurrentPrice]);

  const tableHeader = useMemo(
    () => (
      <OpenPositionsTableHeader
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        filters={filters}
        showFilterInputs={showFilterInputs}
        onSort={handleSort}
        onFilterChange={handleFilterChange}
        renderSortIcon={renderSortIcon}
        renderFilterDropdown={renderFilterDropdown}
      />
    ),
    [
      sortColumn,
      sortDirection,
      filters,
      showFilterInputs,
      renderSortIcon,
      renderFilterDropdown,
    ]
  );

  if (loadingStatus === "loading") {
    return (
      <div className="h-full flex flex-col min-h-0">
        <div className="h-8 px-2 border-b border-border bg-muted/20 flex items-center shrink-0">
          <span className="text-[11px] font-semibold leading-none">
            Open Positions
          </span>
        </div>
        <SkeletonRows spec={POSITIONS_SKELETON} className="pt-1" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Compact header */}
      <div className="h-8 px-2 border-b border-border bg-muted/20 flex items-center justify-between gap-2 shrink-0">
        {/* Left: Title + Stats */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] font-semibold leading-none truncate">
            Open Positions
          </span>
          <div className="flex items-center gap-1 shrink-0">
            <Badge
              variant="outline"
              className="h-4 px-1 py-0 text-[10px] leading-none font-normal"
            >
              {tradesToUse.length} Total
            </Badge>
            <Badge
              variant="outline"
              className="h-4 px-1 py-0 text-[10px] leading-none font-normal text-blue-400 border-blue-400/30"
            >
              {profitableTrades} ↗
            </Badge>
            <Badge
              variant="outline"
              className="h-4 px-1 py-0 text-[10px] leading-none font-normal text-red-400 border-red-400/30"
            >
              {losingTrades} ↘
            </Badge>
          </div>
        </div>

        {/* Right: Actions + Total P&L + Filter Toggle */}
        <div className="flex items-center gap-1.5 shrink-0">
          {isInvestor ? (
            <InvestorBadge />
          ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-1.5 text-[11px] gap-0.5"
                disabled={bulkCounts.all === 0}
                title="Bulk close positions"
              >
                Actions
                <ChevronDown className="h-2.5 w-2.5 ml-0.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Bulk
              </DropdownMenuLabel>
              <DropdownMenuItem
                className="text-red-400 focus:text-red-400 text-[11px]"
                disabled={bulkCounts.all === 0}
                onSelect={() => handleBulkClose("all")}
              >
                Close all positions
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                  {bulkCounts.all}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-400 focus:text-red-400 text-[11px]"
                disabled={bulkCounts.profitable === 0}
                onSelect={() => handleBulkClose("profitable")}
              >
                Close profitable
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                  {bulkCounts.profitable}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-400 focus:text-red-400 text-[11px]"
                disabled={bulkCounts.losing === 0}
                onSelect={() => handleBulkClose("losing")}
              >
                Close losing
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                  {bulkCounts.losing}
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          <Button
            variant={showFilterInputs ? "default" : "ghost"}
            size="sm"
            className="h-5 px-1.5 text-[11px] gap-0.5"
            onClick={toggleFilterInputs}
            title={
              showFilterInputs ? "Hide filter inputs" : "Show filter inputs"
            }
          >
            <Filter className="h-2.5 w-2.5 mr-0.5" />
            {showFilterInputs ? "Hide" : "Filter"}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1.5 text-[11px] text-red-600"
              onClick={clearFilters}
              title="Clear all filters"
            >
              Clear
            </Button>
          )}
          <div
            className={`text-[11px] font-semibold tabular-nums leading-none ${
              liveTotalProfit >= 0 ? "text-blue-400" : "text-red-400"
            }`}
          >
            {formatSignedPnl(liveTotalProfit)} {accountCurrency}
          </div>
        </div>
      </div>

      {processedRows.length === 0 ? (
        <EmptyBlock
          config={{
            glyph: "▦",
            message:
              tradesToUse.length === 0
                ? "No open positions"
                : "No trades match your filters",
            action: hasActiveFilters
              ? { label: "clear all filters", onClick: clearFilters }
              : undefined,
          }}
        />
      ) : (
        <OpenPositionsTable
          rows={processedRows}
          emptyMessage="No open positions"
          header={tableHeader}
          focusedTradeTicket={focusedTradeTicketFromChart}
          bulkCounts={bulkCounts}
          hasCloseByCandidate={hasCloseByCandidate}
          onCloseTrade={handleCloseTrade}
          onCloseBy={handleCloseBy}
          onBulkClose={handleBulkClose}
          onModify={handleModify}
          onClearLevel={handleClearLevel}
          readOnly={isInvestor}
        />
      )}
      <Dialog
        open={!!modifyTrade}
        onOpenChange={(open) => {
          if (!open) setModifyTrade(null);
        }}
      >
        <DialogContent className="max-w-md max-h-[70vh] p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg">
              Modify SL / TP · #{modifyTrade?.ticket}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto">
            {modifyTrade && (
              <TradeOrderScreen
                key={modifyTrade.ticket}
                symbol={modifyTrade.symbol}
                type="modify"
                tradeData={modifyTrade}
                isDesktop
                onBack={() => setModifyTrade(null)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      <BulkCloseConfirmDialog
        open={bulkCloseMode !== null}
        mode={bulkCloseMode}
        count={bulkSelection?.positions.length ?? 0}
        realizedPnl={bulkSelection?.realizedPnl ?? 0}
        symbols={
          bulkSelection
            ? Array.from(
                new Set(bulkSelection.positions.map((p) => p.trade.symbol))
              )
            : []
        }
        currency={accountCurrency}
        onOpenChange={(open) => {
          if (!open) setBulkCloseMode(null);
        }}
        onConfirm={handleConfirmBulkClose}
      />
      <CloseByPositionDialog
        open={!!closeByTrade}
        onOpenChange={(open) => {
          if (!open) setCloseByTrade(null);
        }}
        position={closeByTrade}
      />
    </div>
  );
}
